import { appConfig, getApiBaseCandidates, isAbsoluteHttpUrl, resolveBackendAssetUrl } from '../config/appConfig';

const API_BASE_URL = appConfig.api.baseUrl;

export type CareerRequirement = {
  index: string;
  title: string;
  description: string;
};

export type CareerEntry = {
  id: number;
  group_key: string;
  slug: string;
  kind: 'career';
  language: 'zh' | 'en' | 'ja';
  available_languages: Array<'zh' | 'en' | 'ja'>;
  badge: string;
  title: string;
  summary: string;
  body: string[];
  requirements: CareerRequirement[];
  contact_phone: string;
  contact_email: string;
  qr_hint: string;
  qr_image_url: string | null;
  published_at: string;
  is_pinned: boolean;
  is_default_display: boolean;
};

type CareerListResponse = {
  results: CareerEntry[];
};

type CareerDetailResponse = {
  item: CareerEntry;
  translation_slugs?: Partial<Record<'zh' | 'en' | 'ja', string>>;
};

export class CareerApiError extends Error {
  status?: number;
  kind: 'network' | 'http';

  constructor(message: string, kind: 'network' | 'http', status?: number) {
    super(message);
    this.name = 'CareerApiError';
    this.kind = kind;
    this.status = status;
  }
}

const normalizeEntry = (item: CareerEntry): CareerEntry => ({
  ...item,
  qr_image_url: resolveBackendAssetUrl(item.qr_image_url),
});

const buildApiBases = () => {
  const bases = new Set<string>(getApiBaseCandidates());

  if (isAbsoluteHttpUrl(API_BASE_URL)) {
    bases.add(API_BASE_URL);
  }

  return Array.from(bases).map((base) => base.replace(/\/$/, ''));
};

const fetchJson = async <T>(path: string): Promise<T> => {
  const apiBases = buildApiBases();
  let lastError: unknown = null;

  for (let index = 0; index < apiBases.length; index += 1) {
    try {
      const response = await fetch(`${apiBases[index]}${path}`);
      if (!response.ok) {
        throw new CareerApiError(`Request failed: ${response.status}`, 'http', response.status);
      }
      return response.json() as Promise<T>;
    } catch (error) {
      lastError = error;
      if (index === apiBases.length - 1) {
        break;
      }
    }
  }

  if (lastError instanceof CareerApiError) {
    throw lastError;
  }

  throw new CareerApiError(lastError instanceof Error ? lastError.message : 'Backend is unavailable.', 'network');
};

export const fetchCareerList = async (lang: 'zh' | 'en' | 'ja'): Promise<CareerEntry[]> => {
  const data = await fetchJson<CareerListResponse>(`/careers/?lang=${lang}`);
  return data.results.map(normalizeEntry);
};

export const fetchCareerDetail = async (slug: string, lang: 'zh' | 'en' | 'ja'): Promise<CareerEntry> => {
  const data = await fetchJson<CareerDetailResponse>(`/careers/${slug}/?lang=${lang}`);
  return normalizeEntry(data.item);
};
